import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaEye, FaEyeSlash, FaSpinner, FaLockOpen } from 'react-icons/fa'; // Using react-icons/fa for consistency
import api from '../api/apiService'; // Unified API service


function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false); // Controls visibility for BOTH password fields
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [passwordStrength, setPasswordStrength] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  // Removed email param — token-only reset link is used

  const checkPasswordStrength = (currentPassword) => {
    if (currentPassword.length === 0) return '';
    let strength = 0;
    let feedback = [];

    if (currentPassword.length >= 8) strength += 1; else feedback.push('at least 8 characters');
    if (/[A-Z]/.test(currentPassword)) strength += 1; else feedback.push('an uppercase letter');
    if (/[a-z]/.test(currentPassword)) strength += 1; else feedback.push('a lowercase letter');
    if (/[0-9]/.test(currentPassword)) strength += 1; else feedback.push('a number');
    if (/[!@#$%^&*]/.test(currentPassword)) strength += 1; else feedback.push('a special character');

    if (strength === 5) return 'Strong';
    if (strength >= 3) return 'Medium';
    if (strength >= 1) return 'Weak';
    return feedback.length > 0 ? `Needs: ${feedback.join(', ')}` : 'Too short';
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    setPasswordStrength(checkPasswordStrength(newPassword));
    setError(''); // Clear errors on input change
    setSuccessMsg('');
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
    setError(''); // Clear errors on input change
    setSuccessMsg('');
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');

    // Validation (unchanged)
    if (!password || !confirmPassword) {
      setError('Both new password fields are required.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    const currentStrength = checkPasswordStrength(password);
    if (currentStrength !== 'Strong' && currentStrength !== 'Medium') {
      setError('Password is too weak. Please ensure it meets all requirements.');
      return;
    }

    if (!token) {
      setError('Invalid reset link. Please go through the forgot password process again.');
      return;
    }

    setLoading(true);
    try {
      // <-- CHANGE: send token + newPassword (no email required)
      await api.post('/auth/reset-password', { token, newPassword: password });
      setLoading(false);
      setSuccessMsg('Password has been reset successfully! Redirecting to login...');
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setLoading(false);
      const errorMessage = err.response?.data?.error || 'Failed to reset password. Please try again.';
      setError(errorMessage);
    }
  };

  // Determine password strength indicator color
  const getPasswordStrengthColor = (strength) => {
    switch (strength) {
      case 'Strong': return '#28a745'; // Green
      case 'Medium': return '#ffc107'; // Yellow
      case 'Weak':
      case 'Too short':
      case 'Needs:': // Fallback for specific feedback
        return '#dc3545'; // Red
      default: return '#6c757d'; // Muted grey for empty
    }
  };

  return (
    <div className="d-flex justify-content-center align-items-center bg-light" style={{ minHeight: '100vh' }}>
      <div className="card shadow-lg animate__animated animate__fadeIn" style={cardStyle}>
        <div className="card-body p-4">
          <div className="text-center mb-4">
            <div style={iconContainerStyle}>
              <FaLockOpen size={24} color="#007bff" /> {/* Blue unlock icon */}
            </div>
            <h3 className="card-title mt-3" style={headingStyle}>
              Reset Password
            </h3>
            <p className="text-muted" style={descriptionStyle}>
              Set your new secure password.
            </p>
          </div>

          {successMsg && (
            <div className="alert alert-success text-center animate__animated animate__fadeIn p-2 mb-3" style={successAlertStyle} role="alert">
              {successMsg}
            </div>
          )}
          {error && (
            <div className="alert alert-danger text-center animate__animated animate__fadeIn p-2 mb-3" style={errorAlertStyle} role="alert">
              {error}
            </div>
          )}

          {!token ? (
            <p className="text-center text-danger small mt-4" style={descriptionStyle}>
              <strong className="text-danger">Invalid reset link.</strong> Please go through the{' '}
              <a href="/forgot-password" className="text-decoration-none" style={linkButtonStyle}>
                forgot password
              </a>{' '}
              process again.
            </p>
          ) : (
            <form onSubmit={handleResetPassword}>
              <div className="mb-3">
                <label htmlFor="password" className="form-label" style={labelStyle}>
                  New Password
                </label>
                <div className="input-group">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    className={`form-control ${passwordStrength === 'Strong' ? 'is-valid' : (password.length > 0 && passwordStrength !== 'Strong' && passwordStrength !== 'Medium' ? 'is-invalid' : '')}`}
                    value={password}
                    onChange={handlePasswordChange}
                    style={inputStyle}
                    required
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowPassword(!showPassword)}
                    style={eyeButtonStyle}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <FaEye size={18} /> : <FaEyeSlash size={18} />}
                  </button>
                </div>
                {passwordStrength && (
                  <small className="form-text ms-1" style={{ color: getPasswordStrengthColor(passwordStrength), fontSize: '0.85rem' }}>
                    Password strength: {passwordStrength}
                  </small>
                )}
              </div>

              <div className="mb-3">
                <label htmlFor="confirmPassword" className="form-label" style={labelStyle}>
                  Confirm Password
                </label>
                <div className="input-group">
                    <input
                        type={showPassword ? 'text' : 'password'}
                        id="confirmPassword"
                        className={`form-control ${confirmPassword.length > 0 && password !== confirmPassword ? 'is-invalid' : ''}`}
                        value={confirmPassword}
                        onChange={handleConfirmPasswordChange}
                        style={inputStyle}
                        required
                    />
                     <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setShowPassword(!showPassword)}
                        style={eyeButtonStyle}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                        {showPassword ? <FaEye size={18} /> : <FaEyeSlash size={18} />}
                    </button>
                </div>
                {confirmPassword.length > 0 && password !== confirmPassword && (
                    <small className="text-danger ms-1" style={{ fontSize: '0.85rem' }}>Passwords do not match.</small>
                )}
              </div>

              <button
                type="submit"
                className="btn btn-primary w-100 mt-3"
                style={primaryButtonStyle}
                disabled={loading || !password || !confirmPassword || password !== confirmPassword || passwordStrength === 'Too short' || passwordStrength.startsWith('Needs:')}
              >
                {loading ? <FaSpinner className="fa-spin me-2" /> : 'Reset Password'}
              </button>
            </form>
          )}

          <div className="text-center mt-4 text-muted" style={{ fontSize: '0.9rem' }}>
            <a href="/login" className="text-decoration-none" style={linkButtonStyle}>
              Back to Login
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- UI Styles (Centralized for easier modification) ---

const cardStyle = {
  width: '24rem', // Consistent width
  borderRadius: '1rem', // Softer corners
  background: '#ffffff', // Clean white background
  border: '1px solid #e0e0e0', // Subtle light grey border
  boxShadow: '0 8px 20px rgba(0, 0, 0, 0.08)', // Lighter, more diffused shadow
  color: '#333333', // Standard dark text
  animationDuration: '0.5s' // For animate.css fade-in
};

const headingStyle = {
  fontWeight: '600', // Semi-bold
  fontSize: '1.8rem', // Slightly larger
  color: '#333333', // Darker heading color
  marginBottom: '0.5rem',
};

const descriptionStyle = {
  fontSize: '0.95rem',
  color: '#6c757d', // Muted grey for descriptions
};

const iconContainerStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#e9f5ff', // Light blue background for icon
  borderRadius: '50%',
  padding: '0.8rem',
  border: '1px solid #cce5ff', // Lighter blue border
  width: '50px', // Slightly larger icon container
  height: '50px',
  marginBottom: '1rem',
};

const labelStyle = {
  color: '#495057', // Darker grey for labels
  fontSize: '0.9rem',
  fontWeight: '500', // Medium weight for labels
};

const inputStyle = {
  borderRadius: '0.4rem', // Slightly less rounded than card
  backgroundColor: '#f8f9fa', // Very light grey input background
  border: '1px solid #dee2e6', // Standard light grey input border
  color: '#343a40', // Dark text for input
  fontSize: '1rem',
  padding: '0.75rem 1rem', // More padding for better feel
  boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.04)', // Subtle inset shadow
  transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
};

const primaryButtonStyle = {
  borderRadius: '0.5rem',
  padding: '0.8rem 1.5rem',
  fontWeight: 'bold',
  fontSize: '1.05rem',
  backgroundColor: '#007bff', // Standard Bootstrap primary blue
  border: 'none',
  boxShadow: '0 4px 10px rgba(0, 123, 255, 0.2)', // Blue shadow for primary button
  cursor: 'pointer',
  transition: 'background-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out, transform 0.1s ease',
};

const linkButtonStyle = {
  color: '#007bff', // Standard Bootstrap primary blue for links
  textDecoration: 'none',
  fontSize: '0.9rem',
  transition: 'color 0.2s ease-in-out',
};

const successAlertStyle = {
  backgroundColor: '#d4edda', // Light green for success
  color: '#155724', // Dark green text
  fontSize: '0.9rem',
  borderRadius: '0.5rem',
  border: '1px solid #c3e6cb',
};

const errorAlertStyle = {
  backgroundColor: '#f8d7da', // Light red for error
  color: '#721c24', // Dark red text
  fontSize: '0.9rem',
  borderRadius: '0.5rem',
  border: '1px solid #f5c6cb',
};

const eyeButtonStyle = {
  borderTopLeftRadius: '0',
  borderBottomLeftRadius: '0',
  borderColor: '#dee2e6',
  backgroundColor: '#e9ecef', // Light grey background for eye icon button
  color: '#6c757d', // Muted icon color
  transition: 'background-color 0.2s ease-in-out, border-color 0.2s ease-in-out',
};


export default ResetPassword;
